/*
 * My Dental Products cart drawer logic.
 * 1. Gift sync: every non-gift cart line whose variant has custom.gift_product, or whose product is a
 *    trigger of an active "gift_rule" block, gets that gift (first available variant) in the cart
 *    exactly once, quantity 1, with line property _gift = "true".
 *    Waterflosser: the free PAP+ strips come once per waterflosser, at most 2.
 *    Duo-set (bundle of 2 waterflossers, product DUO_SET): always 2x PAP+ strips (1 verpakking), matching
 *    the automatic discount "Cadeau: 2x gratis PAP+ strips bij Duo-set".
 *    Gift lines without a remaining trigger line are removed.
 * 2. Grouped quantity controls (+, -, remove) in the drawer, using /cart/update.js on all line keys.
 * 3. Upsell add buttons and variant selects.
 * 4. Hides the Klaviyo teaser while the drawer is open.
 * Works with the Impact theme events: cart:change (detail.cart with sections) and cart:refresh,
 * and also reacts to any other script calling /cart/add, /cart/change, /cart/update or /cart/clear.
 */
(function () {
  if (window.__mdpCartDrawer) return;
  window.__mdpCartDrawer = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var DEBUG = /mdp_cart_debug/.test(window.location.search);
  var nativeFetch = window.fetch.bind(window);
  var CART_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var WATERFLOSSER = 8021815886010, PAP_STRIPS = 8337766383802, DUO_MAX_GIFTS = 2;
  var DUO_SET = 10413241663674, DUO_GIFT_VARIANT = 45630627774650, DUO_SET_GIFTS = 2;

  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[mdp-cart]'].concat([].slice.call(arguments)));
  }

  function isCartPage() {
    return !!(window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart');
  }

  function drawer() {
    return document.getElementById('cart-drawer');
  }

  function sectionId() {
    var d = drawer();
    var s = d && d.closest('.shopify-section');
    return s ? s.id.replace('shopify-section-', '') : 'cart-drawer';
  }

  function bundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    var id = sectionId();
    if (sections.indexOf(id) === -1) sections.push(id);
    return sections;
  }

  function parseState(source) {
    if (!source) return null;
    var doc = typeof source === 'string' ? new DOMParser().parseFromString(source, 'text/html') : source;
    var el = doc.querySelector('[data-mdp-cart-json]');
    if (!el) return { item_count: 0, lines: [] };
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      log('json parse error', e);
      return null;
    }
  }

  function postJSON(path, body) {
    return nativeFetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
  }

  async function fetchState() {
    var id = sectionId();
    var res = await nativeFetch(root + '?sections=' + encodeURIComponent(id));
    var json = await res.json();
    return parseState(json[id] || '');
  }

  /* Fetch the cart and the bundled sections, then let the theme re-render the drawer. */
  async function publishCart() {
    if (isCartPage()) {
      window.location.reload();
      return null;
    }
    var sections = bundledSections();
    var results = await Promise.all([
      nativeFetch(root + 'cart.js').then(function (r) { return r.json(); }),
      nativeFetch(root + '?sections=' + sections.map(encodeURIComponent).join(',')).then(function (r) { return r.json(); })
    ]);
    var cart = results[0];
    cart.sections = results[1];
    /* Show the empty state right away; the theme animates it in afterwards. */
    if (cart.item_count === 0 && cart.sections[sectionId()] && drawer()) {
      var emptyDoc = new DOMParser().parseFromString(cart.sections[sectionId()], 'text/html');
      var emptyDrawer = emptyDoc.querySelector('#cart-drawer');
      if (emptyDrawer) drawer().replaceChildren.apply(drawer(), Array.prototype.slice.call(emptyDrawer.childNodes));
    }
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart, mdp: true } })
    );
    return cart;
  }

  /* Run cart mutations one after another. */
  var queue = Promise.resolve();
  function exclusive(fn) {
    var run = queue.then(fn, fn);
    queue = run.catch(function () {});
    return run;
  }

  /* ---------------- Public API (used by assets/mdp-routine-addons.js) ---------------- */

  function openDrawer() {
    var d = drawer();
    if (!d || isCartPage() || d.hasAttribute('open')) return;
    if (typeof d.show === 'function') d.show();
    else d.setAttribute('open', '');
  }

  /* Adds items in ONE /cart/add.js call, syncs gifts, re-renders and opens the drawer.
     Resolves to { ok, status, json, cart }. */
  function addItems(items) {
    return exclusive(async function () {
      var res = await postJSON('cart/add.js', { items: items });
      var json = await res.json().catch(function () { return {}; });
      if (!res.ok) return { ok: false, status: res.status, json: json, cart: null };
      var cart = null;
      try {
        await applyGiftOps(await fetchState());
        cart = await publishCart();
      } catch (e) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
      openDrawer();
      return { ok: true, status: res.status, json: json, cart: cart };
    });
  }

  window.MDPCart = {
    add: addItems,
    open: openDrawer,
    refresh: function () {
      return exclusive(function () { return publishCart(); });
    }
  };

  /* ---------------- Gift sync ---------------- */

  var timer = null;
  var latestState = null;
  var opLog = [];
  var failedGiftVariants = {};

  /* Gifts are matched per gift product, so a customer can swap the gift to another colour (variant). */
  function planGiftOps(state) {
    var lines = (state && state.lines) || [];
    var desired = {};
    var want = {};
    var order = [];
    var wfQty = 0;
    lines.forEach(function (l) {
      if (!l.gift_line && l.product_id === WATERFLOSSER) wfQty += l.quantity;
    });
    lines.forEach(function (l) {
      if (l.gift_line) return;
      var gifts = Array.isArray(l.gifts) ? l.gifts : l.gift ? [l.gift] : [];
      if (l.product_id === DUO_SET) gifts = gifts.concat([{ variant_id: DUO_GIFT_VARIANT, product_id: PAP_STRIPS, available: true, duo: true }]);
      gifts.forEach(function (g) {
        if (!g || !g.variant_id || !g.available) return;
        var vid = String(g.variant_id);
        if (failedGiftVariants[vid]) return;
        var gk = g.product_id ? 'p' + g.product_id : 'v' + vid;
        var q = g.duo ? DUO_SET_GIFTS : (l.product_id === WATERFLOSSER && g.product_id === PAP_STRIPS) ? Math.max(1, Math.min(wfQty, DUO_MAX_GIFTS)) : 1;
        if (!desired[gk]) {
          desired[gk] = g;
          want[gk] = q;
          order.push(gk);
        } else if (q > want[gk]) {
          want[gk] = q;
        }
      });
    });

    var kept = {};
    var ops = [];
    lines.forEach(function (l) {
      if (!l.gift_line) return;
      var gk = desired['p' + l.product_id] ? 'p' + l.product_id : 'v' + l.variant_id;
      if (desired[gk] && !kept[gk]) {
        kept[gk] = true;
        if (l.quantity !== want[gk]) ops.push({ type: 'change', key: l.key, quantity: want[gk] });
      } else {
        ops.push({ type: 'change', key: l.key, quantity: 0 });
      }
    });

    order.forEach(function (gk) {
      if (!kept[gk]) ops.push({ type: 'add', variantId: Number(desired[gk].variant_id), quantity: want[gk] || 1 });
    });
    return ops;
  }

  function rateLimited(count) {
    var now = Date.now();
    opLog = opLog.filter(function (t) { return now - t < 15000; });
    if (opLog.length + count > 12) return true;
    for (var i = 0; i < count; i++) opLog.push(now);
    return false;
  }

  /* Applies the gift operations for a state. Returns true when the cart changed. */
  async function applyGiftOps(state) {
    if (!state) return false;
    var ops = planGiftOps(state);
    log('gift ops', ops);
    if (!ops.length) return false;
    if (rateLimited(ops.length)) {
      if (window.console) console.warn('[mdp-cart] gift sync paused: too many cart updates in a short time');
      return false;
    }
    var changed = false;
    for (var i = 0; i < ops.length; i++) {
      var op = ops[i];
      var res;
      if (op.type === 'change') {
        res = await postJSON('cart/change.js', { id: op.key, quantity: op.quantity });
      } else {
        res = await postJSON('cart/add.js', {
          items: [{ id: op.variantId, quantity: op.quantity || 1, properties: { _gift: 'true' } }]
        });
        if (!res.ok) failedGiftVariants[String(op.variantId)] = true;
      }
      if (res.ok) changed = true;
    }
    return changed;
  }

  function runGiftSync(state) {
    return exclusive(async function () {
      var changed = false;
      try {
        changed = await applyGiftOps(state || (await fetchState()));
      } catch (e) {
        if (window.console) console.warn('[mdp-cart] gift sync error', e);
      }
      if (changed) {
        try {
          await publishCart();
        } catch (e) {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      }
    });
  }

  /* The theme re-renders the drawer 1250ms after a line item change, so wait longer in that case
     to avoid the theme overwriting our newer render with older HTML. */
  function scheduleGiftSync(state, delay) {
    latestState = state;
    clearTimeout(timer);
    timer = setTimeout(function () {
      var s = latestState;
      latestState = null;
      runGiftSync(s);
    }, typeof delay === 'number' ? delay : 250);
  }

  document.addEventListener('cart:change', function (event) {
    var cart = event.detail && event.detail.cart;
    var html = cart && cart.sections ? cart.sections[sectionId()] : null;
    var delay = event.detail && event.detail.baseEvent === 'line-item:change' ? 1400 : 250;
    scheduleGiftSync(html ? parseState(html) : null, delay);
  });

  document.addEventListener('cart:refresh', function () {
    scheduleGiftSync(null);
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) scheduleGiftSync(null);
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') scheduleGiftSync(null, 400);
  });

  /* Detect cart changes made by any other script (apps, quick add, etc.). Our own requests use nativeFetch. */
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var promise = nativeFetch(input, init);
    if (CART_URL_RE.test(url)) {
      promise.then(function () { scheduleGiftSync(null, 1400); }).catch(function () {});
    }
    return promise;
  };

  /* ---------------- Drawer open state ---------------- */

  function onDrawerOpenChange(isOpen) {
    document.documentElement.classList.toggle('mdp-cart-open', isOpen);
    if (isOpen) scheduleGiftSync(null);
  }

  function watchDrawerOpen() {
    var d = drawer();
    if (!d || d.__mdpObserved) return;
    d.__mdpObserved = true;
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'open') onDrawerOpenChange(d.hasAttribute('open'));
      });
    }).observe(d, { attributes: true, attributeFilter: ['open'] });
    if (d.hasAttribute('open')) onDrawerOpenChange(true);
  }

  function init() {
    watchDrawerOpen();
    var d = drawer();
    if (d) scheduleGiftSync(parseState(d));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---------------- Quantity controls ---------------- */

  function uniqueKeys(value) {
    var seen = {};
    return String(value || '')
      .split('|')
      .filter(function (k) {
        if (!k || seen[k]) return false;
        seen[k] = true;
        return true;
      });
  }

  function setGroupQuantity(controls, quantity) {
    var keys = uniqueKeys(controls.getAttribute('data-keys'));
    if (!keys.length) return;
    var line = controls.closest('line-item');
    if (line) line.setAttribute('aria-busy', 'true');

    var updates = {};
    keys.forEach(function (k, i) {
      updates[k] = i === 0 ? quantity : 0;
    });

    return exclusive(async function () {
      try {
        var res = await postJSON('cart/update.js', { updates: updates });
        if (!res.ok) {
          var err = await res.json().catch(function () { return {}; });
          if (line) {
            line.removeAttribute('aria-busy');
            var info = line.querySelector('.mdp-line__info');
            var old = info && info.querySelector('.mdp-line__error');
            if (old) old.remove();
            if (info) {
              info.insertAdjacentHTML(
                'beforeend',
                '<p class="mdp-up__error mdp-line__error" role="alert">' + escapeHtml(err.description || 'Aanpassen lukt nu niet. Probeer het opnieuw.') + '</p>'
              );
            }
          }
          return;
        }
        /* Fix gifts before re-rendering so removed triggers never leave a stale gift on screen. */
        await applyGiftOps(await fetchState());
        await publishCart();
      } catch (e) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target.closest && event.target.closest('[data-mdp-step], [data-mdp-remove]');
    if (!target) return;
    var controls = target.closest('[data-mdp-qty-controls]');
    if (!controls) return;
    event.preventDefault();
    var qty = parseInt(controls.getAttribute('data-qty'), 10) || 0;
    var max = parseInt(controls.getAttribute('data-max'), 10);
    var next;
    if (target.hasAttribute('data-mdp-remove')) {
      next = 0;
    } else {
      next = qty + parseInt(target.getAttribute('data-mdp-step'), 10);
      if (!isNaN(max)) next = Math.min(next, max);
      next = Math.max(next, 0);
    }
    if (next === qty) return;
    setGroupQuantity(controls, next);
  });

  /* ---------------- Upsells ---------------- */

  function escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  document.addEventListener('change', function (event) {
    var select = event.target.closest && event.target.closest('[data-mdp-upsell-select]');
    if (!select) return;
    var card = select.closest('[data-mdp-upsell]');
    var opt = select.options[select.selectedIndex];
    if (!card || !opt) return;
    var priceEl = card.querySelector('[data-mdp-upsell-price]');
    var compareEl = card.querySelector('[data-mdp-upsell-compare]');
    var button = card.querySelector('[data-mdp-upsell-add]');
    if (priceEl) priceEl.innerHTML = '<span class="sr-only">Prijs</span>' + escapeHtml(opt.getAttribute('data-price'));
    if (compareEl) {
      var compare = opt.getAttribute('data-compare');
      compareEl.innerHTML = compare ? '<span class="sr-only">Normale prijs</span>' + escapeHtml(compare) : '';
      compareEl.hidden = !compare;
    }
    if (button) button.setAttribute('data-variant-id', select.value);
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-mdp-upsell-add]');
    if (!button) return;
    event.preventDefault();
    if (button.getAttribute('aria-busy') === 'true') return;

    var card = button.closest('[data-mdp-upsell]');
    var select = card && card.querySelector('[data-mdp-upsell-select]');
    var variantId = Number(select ? select.value : button.getAttribute('data-variant-id'));
    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');
    var oldError = card && card.querySelector('.mdp-up__error');
    if (oldError) oldError.remove();

    exclusive(async function () {
      try {
        var item = { id: variantId, quantity: 1 };
        var source = button.getAttribute('data-source');
        if (source) item.properties = { _bron: source };
        var res = await postJSON('cart/add.js', { items: [item] });
        if (!res.ok) {
          var err = await res.json().catch(function () { return {}; });
          if (card) {
            card.querySelector('.mdp-up__body').insertAdjacentHTML(
              'beforeend',
              '<p class="mdp-up__error" role="alert">' + escapeHtml(err.description || 'Toevoegen lukt nu niet. Probeer het opnieuw.') + '</p>'
            );
          }
          return;
        }
        await applyGiftOps(await fetchState());
        await publishCart();
      } catch (e) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        button.removeAttribute('aria-busy');
      }
    });
  });

  /* ---------------- Gift colour choice ---------------- */

  document.addEventListener('click', function (event) {
    var opt = event.target.closest && event.target.closest('[data-mdp-gift-swap]');
    if (!opt) return;
    event.preventDefault();
    if (opt.getAttribute('aria-pressed') === 'true') return;
    var key = opt.getAttribute('data-key');
    var variantId = Number(opt.getAttribute('data-variant-id'));
    if (!key || !variantId) return;
    var line = opt.closest('line-item');
    if (line) line.setAttribute('aria-busy', 'true');
    exclusive(async function () {
      try {
        var res = await postJSON('cart/add.js', {
          items: [{ id: variantId, quantity: 1, properties: { _gift: 'true' } }]
        });
        if (res.ok) await postJSON('cart/change.js', { id: key, quantity: 0 });
        await publishCart();
      } catch (e) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    });
  });
})();